import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { GoogleAuthProvider, signInWithPopup, signInWithRedirect, getRedirectResult } from 'firebase/auth';
import { collection, query, orderBy, limit, getDocs, setDoc, deleteDoc, doc, updateDoc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase/firebase';
import { updateProfile } from 'firebase/auth';
import { motion, AnimatePresence } from 'framer-motion';
import './MyStatsPage.css';

/**
 * UTILS & NORMALIZATION
 */

// Get Central Time DateKey "YYYY-MM-DD"
const getCTDateKey = (date = new Date()) => {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Chicago',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).format(date);
};

// Main function to determine today's Chronologicle day number
const toCentralMidnight = (date) => {
    const central = new Date(
        date.toLocaleString("en-US", { timeZone: "America/Chicago" })
    );
    central.setHours(0, 0, 0, 0);
    return central;
};

// Normalize Firestore document to expected schema
const normalizeGame = (docData, id) => {
    // If dateKey is the doc ID, use it. Otherwise look in data.
    const dateKey = docData.dateKey || id;
    return {
        id,
        dateKey,
        mode: docData.mode || (docData.isPremiumMode ? "premium" : "daily"),
        score: docData.score ?? docData.finalScore ?? 0,
        numGuesses: docData.numGuesses ?? 0,
        events: docData.events || [],
        timestamp: docData.timestamp || Date.now(),
    };
};

const formatYear = (year) => {
    if (year === null || year === undefined) return "N/A";
    return year < 0 ? `${Math.abs(year)} BCE` : `${year}`;
};

/**
 * COMPONENTS
 */

const ChevronLeft = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="m15 18-6-6 6-6" />
    </svg>
);

const ChevronDown = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <path d="m6 9 6 6 6-6" />
    </svg>
);


const MyStatsPage = ({ onReturn, testData = null }) => {
    const [user, setUser] = useState(() => testData?.user ?? null);
    const [loading, setLoading] = useState(() => testData?.loading ?? !testData);
    const [history, setHistory] = useState(() => testData?.history ?? []);
    const [expandedId, setExpandedId] = useState(() => testData?.expandedId ?? null);

    // Name Editing State
    const [isEditingName, setIsEditingName] = useState(false);
    const [newName, setNewName] = useState(() => testData?.user?.displayName || '');
    const [isSavingName, setIsSavingName] = useState(false);
    const [startDate, setStartDate] = useState(() => testData?.startDate ?? null);

    const fetchHistory = useCallback(async (uid) => {
        try {
            setLoading(true);
            const scoresRef = collection(db, "users", uid, "scores");
            const q = query(scoresRef, orderBy("timestamp", "desc"), limit(500));
            const snap = await getDocs(q);

            const games = snap.docs.map(doc => normalizeGame(doc.data(), doc.id));
            setHistory(games);
        } catch (err) {
            console.error("Error fetching stats:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (testData) return undefined;

        const unsubscribe = auth.onAuthStateChanged((u) => {
            setUser(u);
            if (u) {
                setNewName(u.displayName || '');
                fetchHistory(u.uid);
            } else {
                setLoading(false);
            }
        });

        const fetchConfig = async () => {
            try {
                const configRef = doc(db, "config", "gameState");
                const snap = await getDoc(configRef);
                if (snap.exists()) {
                    setStartDate(snap.data().startDate);
                }
            } catch (err) {
                console.warn("Failed to fetch startDate:", err);
            }
        };
        fetchConfig();

        return () => unsubscribe();
    }, [fetchHistory, testData]);

    const handleNameUpdate = async () => {
        const trimmed = newName.trim();
        if (!trimmed || trimmed === user.displayName) {
            setIsEditingName(false);
            return;
        }

        try {
            setIsSavingName(true);
            // 1. Update Firebase Auth
            await updateProfile(auth.currentUser, { displayName: trimmed });

            // 2. Update local state
            setUser({ ...auth.currentUser });

            // 3. Update Firestore (Leaderboard + User History)
            const dateKey = getCTDateKey();
            const uid = auth.currentUser.uid;

            // Update Leaderboard
            const leaderboardRef = doc(db, "leaderboards", dateKey, "players", uid);
            const lbSnap = await getDoc(leaderboardRef);
            if (lbSnap.exists()) {
                await updateDoc(leaderboardRef, { name: trimmed });
            }

            // Update User Score History
            const scoreRef = doc(db, "users", uid, "scores", dateKey);
            const scoreSnap = await getDoc(scoreRef);
            if (scoreSnap.exists()) {
                await updateDoc(scoreRef, { name: trimmed });
            }

            setIsEditingName(false);
        } catch (err) {
            console.error("Error updating name:", err);
            alert("Failed to update name. Please try again.");
        } finally {
            setIsSavingName(false);
        }
    };

    const getDayNumber = (itemDateKey) => {
        if (!startDate || !itemDateKey) return null;
        try {
            const start = toCentralMidnight(new Date(startDate));
            // dateKey is YYYY-MM-DD, which localizes to midnight depending on implementation
            // so we'll be careful to treat it as a local date string for the midnight conversion
            const dParts = itemDateKey.split('-');
            const current = toCentralMidnight(new Date(dParts[0], dParts[1] - 1, dParts[2]));

            const msPerDay = 1000 * 60 * 60 * 24;
            const diff = Math.floor((current - start) / msPerDay);
            return Math.max(1, diff + 1);
        } catch (e) {
            return null;
        }
    };

    const stats = useMemo(() => {
        if (!history.length) return null;

        // All games for stats
        const relevantHistory = history
            .sort((a, b) => b.dateKey.localeCompare(a.dateKey));

        // Average score (All games)
        const avgScore = relevantHistory.length
            ? Math.round(relevantHistory.reduce((sum, g) => sum + g.score, 0) / relevantHistory.length)
            : 0;

        // Best Score (All games)
        const bestScore = relevantHistory.length > 0
            ? Math.max(...relevantHistory.map(g => g.score))
            : 0;

        // Streaks (All games)
        const uniqueDays = Array.from(new Set(relevantHistory.map(g => g.dateKey)));

        let currentStreak = 0;
        let longestStreak = 0;

        if (uniqueDays.length > 0) {
            const today = getCTDateKey();
            const yesterday = getCTDateKey(new Date(Date.now() - 86400000));

            // If haven't played today OR yesterday, streak is 0
            if (uniqueDays[0] === today || uniqueDays[0] === yesterday) {
                let tempStreak = 1;
                for (let i = 0; i < uniqueDays.length - 1; i++) {
                    const d1 = new Date(uniqueDays[i]);
                    const d2 = new Date(uniqueDays[i + 1]);
                    const diffDays = Math.round((d1 - d2) / (1000 * 60 * 60 * 24));

                    if (diffDays === 1) {
                        tempStreak++;
                    } else {
                        break;
                    }
                }
                currentStreak = tempStreak;
            }

            // Longest streak
            let maxS = 0;
            let currS = 1;
            for (let i = 0; i < uniqueDays.length - 1; i++) {
                const d1 = new Date(uniqueDays[i]);
                const d2 = new Date(uniqueDays[i + 1]);
                const diffDays = Math.round((d1 - d2) / (1000 * 60 * 60 * 24));
                if (diffDays === 1) {
                    currS++;
                } else {
                    maxS = Math.max(maxS, currS);
                    currS = 1;
                }
            }
            longestStreak = Math.max(maxS, currS);
        }

        const last14Scores = relevantHistory.slice(0, 14).reverse().map(g => g.score);

        return {
            avgScore,
            bestScore,
            currentStreak,
            longestStreak,
            totalGames: relevantHistory.length,
            last14Scores
        };
    }, [history]);

    // Unified logic to process a user (from popup or redirect)
    const processUserTransfer = useCallback(async (user) => {
        // Enforce First Name Only
        const fullName = user.displayName || "";
        const firstName = fullName.split(" ")[0];

        if (firstName && firstName !== fullName) {
            try {
                await updateProfile(user, { displayName: firstName });
                // Update local user object to reflect change immediately
                user.displayName = firstName;
            } catch (err) {
                console.warn("Profile update failed in stats transfer", err);
            }
        }

        // 1. Check for Guest Score
        const guestUid = localStorage.getItem("guestUid");
        if (guestUid) {
            const dateKey = getCTDateKey();
            const guestLeaderboardRef = doc(db, "leaderboards", dateKey, "players", guestUid);
            const guestSnap = await getDoc(guestLeaderboardRef);

            if (guestSnap.exists()) {
                const guestData = guestSnap.data();

                // 2. Prepare User Entry
                const userEntry = {
                    ...guestData,
                    name: user.displayName || "Player",
                    type: "user",
                    userId: user.uid,
                };

                // 3. Write to User's Permanent Score History
                const userScoreRef = doc(db, "users", user.uid, "scores", dateKey);
                await setDoc(userScoreRef, userEntry);

                // 4. Update Leaderboard Entry (Delete Guest -> Create User)
                const userLeaderboardRef = doc(db, "leaderboards", dateKey, "players", user.uid);
                await setDoc(userLeaderboardRef, userEntry);
                await deleteDoc(guestLeaderboardRef); // Remove "Guest" entry
            }
        }

        // 6. Explicitly refresh history to show the newly transferred score
        await fetchHistory(user.uid);
        setLoading(false);
    }, [fetchHistory]);

    // Handle Redirect Return
    useEffect(() => {
        if (testData) return undefined;

        const checkRedirect = async () => {
            try {
                const result = await getRedirectResult(auth);
                if (result && result.user) {
                    // Check if we were pending a transfer
                    if (localStorage.getItem("pendingTransfer")) {
                        setLoading(true);
                        await processUserTransfer(result.user);
                        localStorage.removeItem("pendingTransfer");
                    }
                }
            } catch (err) {
                console.error("Redirect return error:", err);
                setLoading(false);
            }
        };
        checkRedirect();
    }, [processUserTransfer, testData]);


    const handleLoginAndTransfer = async () => {
        try {
            setLoading(true);
            const provider = new GoogleAuthProvider();

            // Try Popup First
            const result = await signInWithPopup(auth, provider);
            await processUserTransfer(result.user);

        } catch (error) {
            // Fallback to Redirect for common mobile blockers
            if (error.code !== 'auth/popup-closed-by-user' && error.code !== 'auth/cancelled-popup-request') {

                localStorage.setItem("pendingTransfer", "true");
                try {
                    await signInWithRedirect(auth, new GoogleAuthProvider());
                } catch (redirectErr) {
                    console.error("Redirect failed", redirectErr);
                    setLoading(false);
                }
                return; // Execution stops here as page redirects
            }

            console.error("Transfer Login Failed:", error);
            alert("Login failed. Please try again.");
            setLoading(false);
        }
    };

    if (loading) return <div className="stats-container loading" style={{ fontWeight: "100", fontSize: "1.5rem" }}>Loading Stats...</div>;
    if (!user) return (
        <div className="stats-container promo">
            <h1>My Stats</h1>
            <p className='promo-text'>Log in to track your game history.</p>
            <button
                className="return-btn"
                onClick={handleLoginAndTransfer}
                style={{ marginBottom: '1rem', background: '#e7c17f', color: '#002d21' }}
            >
                Log in with Google
            </button>
            <button className="return-btn" onClick={onReturn}>Return Home</button>
            <button className="back-arrow" onClick={onReturn}><ChevronLeft /></button>
        </div>
    );

    return (
        <div className="stats-container">
            <header className="stats-header">
                <button className="back-arrow" onClick={onReturn}><ChevronLeft /></button>
                <div className="center-title">
                    {isEditingName ? (
                        <input
                            type="text"
                            className="name-edit-input"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            onBlur={handleNameUpdate}
                            onKeyDown={(e) => e.key === 'Enter' && handleNameUpdate()}
                            autoFocus
                            disabled={isSavingName}
                            maxLength={20}
                        />
                    ) : (
                        <h1 onClick={() => setIsEditingName(true)} title="Click to edit name">
                            {user.displayName || "Chronologler"}
                        </h1>
                    )}
                </div>
            </header>

            <div style={{ flex: "0 0 auto", paddingBottom: "1rem" }}>
                {/* HEADLINE STATS (Now Fixed) */}
                <section className="stats-grid">
                    <div className="stat-card">
                        <label>High <br /> Score</label>
                        <div className="value-row">
                            <span className="value">{stats?.bestScore || 0}</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <label>Average <br /> Score</label>
                        <div className="value-row">
                            <span className="value">{stats?.avgScore || 0}</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <label>Games <br /> Played</label>
                        <div className="value-row">
                            <span className="value">{stats?.totalGames || 0}</span>
                        </div>
                    </div>
                </section>
            </div>

            <div className="scrollable-content">


                {/* HISTORY LIST */}
                <section className="history-list">
                    <h3 style={{ marginBottom: "0.75rem", marginTop: "0rem" }}>Recent Games</h3>
                    {!history.length && <p className="empty-msg">No games found for this filter.</p>}
                    {history.map((game) => (
                        <div
                            key={game.id}
                            className={`history-item ${expandedId === game.id ? 'expanded' : ''}`}
                        >
                            <div className="item-summary" onClick={() => setExpandedId(expandedId === game.id ? null : game.id)}>
                                <div className="left">
                                    <span className="date">
                                        {getDayNumber(game.dateKey) ? `Chronologicle #${getDayNumber(game.dateKey)}` : "Chronologicle"}
                                    </span>
                                </div>
                                <div className="right">
                                    <span className="score">{game.score}</span>
                                    <span className="expand-indicator">
                                        <motion.span
                                            animate={{ rotate: expandedId === game.id ? 180 : 0 }}
                                            transition={{ duration: 0.3 }}
                                            style={{ display: "inline-block" }}
                                        >
                                            <ChevronDown />
                                        </motion.span>
                                    </span>
                                </div>
                            </div>

                            <AnimatePresence initial={false}>
                                {expandedId === game.id && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{
                                            height: { duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] },
                                            opacity: { duration: 0.2 }
                                        }}
                                        style={{ overflow: "hidden" }}
                                    >
                                        <div style={{ padding: "1rem 0" }}>
                                            <table className="history-table">
                                                <colgroup>
                                                    <col className="history-event" />
                                                    <col className="history-guess" />
                                                    <col className="history-actual" />
                                                    <col className="history-points" />
                                                </colgroup>
                                                <thead>
                                                    <tr style={{ textAlign: "left", borderBottom: "1px solid rgba(231, 193, 127, 0.3)" }}>
                                                        <th>Event</th>
                                                        <th>Guess</th>
                                                        <th>Actual</th>
                                                        <th><span className="points-label-full">Points</span><span className="points-label-short">Pts</span></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {game.events.map((e, idx) => (
                                                        <tr key={idx} style={{ color: "#e7c17f" }}>
                                                            <td
                                                                className="history-event-cell"
                                                                title={e.event}
                                                            >
                                                                {e.event}
                                                            </td>
                                                            <td>{formatYear(e.guess)}</td>
                                                            <td>{formatYear(e.actual)}</td>
                                                            <td style={{ fontWeight: "bold" }}>{e.points}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    ))}
                </section>
            </div>
        </div>
    );
};

export default MyStatsPage;
